import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

interface EditableInputProps {
  className?: string
  value?: string
  onChange: (text: string) => Promise<void> | void
  placeholder?: string
  disabled?: boolean
  defaultValue?: string
  required?: boolean
}

export default function EditableInput({
  className,
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  required = false,
}: EditableInputProps) {
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(value)

  const inputRef = useRef<HTMLInputElement>(null)

  async function onConfirm() {
    setEditing(false)

    // If required validation fails, fallback to original value
    if (required && !text.trim()?.length) {
      setText(value)
      return
    }

    if (text === value) return

    setLoading(true)
    try {
      await onChange(text)
    } catch {
      // On error, fallback to original value
      setText(value)
    }
    setLoading(false)
    inputRef.current?.blur()
  }

  function onCancel() {
    setEditing(false)
    inputRef.current?.setSelectionRange(0, 0)
    inputRef.current?.blur()
    setText(value)
  }

  function onKeydown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') onConfirm()
    if (event.key === 'Escape') onCancel()
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.select()
    }
  }, [editing])

  useEffect(() => {
    setText(value)
  }, [value])

  if (disabled) {
    return <div className={['text-left', className].join(' ')}>{text}</div>
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={[
          'w-full bg-transparent focus:outline-hidden focus:ring-3 rounded-sm text-left',
          className,
        ].join(' ')}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={onConfirm}
        onKeyDown={onKeydown}
      />
    )
  }

  return (
    <button
      className={[
        'bg-transparent text-left cursor-text decoration-dashed decoration-white/50 rounded-sm break-words',
        loading
          ? 'bg-animated from-transparent to-white/20 pointer-events-none'
          : '',
        className,
      ].join(' ')}
      onClick={() => setEditing(true)}
      onFocus={() => setEditing(true)}
    >
      {text ? (
        <span>{text}</span>
      ) : (
        <span className="opacity-0 group-hover:opacity-40 transition-opacity">
          {placeholder}
        </span>
      )}
    </button>
  )
}
